import { useMemo } from "react";
import { useParams } from "react-router-dom";
import useSWR from "swr";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { SelectionList } from "./selections";
import { SkuDetailPanel } from "./selections/detail";
import { StaticSkuDetailPanel } from "./selections/detail/StaticSkuDetailPanel";
import { ToningEditor } from "../components/ToningEditor";
import { ToningDetailPanel } from "../components/ToningDetailPanel";
import { AnimationEditor } from "../../components/AnimationEditor";
import { SkinViewer } from "../../components/SkinViewer";
import { ColorSetDetailPanel } from "../../components/ColorSetDetailPanel";
import { SelectionDetailPanel } from "../../components/SelectionDetailPanel";
import { InfoPanel } from "./info-panel";
import { RelatedTemplatesPanel } from "./related-templates";
import { ChildTemplatesPanel } from "./child-templates";
import { useConfigStore } from "../../store/configStore";
import { useEditorStore } from "../../store/editorStore";
import { Badge } from "@/components/ui/badge";
import {
  Layers,
  Palette,
  Play,
  Component,
  Info,
  Link2,
  Package,
  GitBranch,
} from "lucide-react";
import { DefaultSetOutfitPanel } from "./set-panel";
import {
  SpineTemplate,
  isChildSpineTemplate,
} from "../../types/spine-template";

/** 常规 Spine 模版：关联 / 调色 / 动画 / Skin 等全量 Tab */
export function SpineConfigPanel() {
  const { tid } = useParams<{ tid: string }>();
  const { data: template } = useSWR<SpineTemplate>(
    tid ? [`/spine-templates/${tid}`] : null,
  );
  // 未加载到模版数据前先不展示，避免子模版闪一下 Tab
  const showChildrenTab = Boolean(template) && !isChildSpineTemplate(template);
  const templateConfig = useConfigStore((s) => s.templateConfig);
  const spineData = useConfigStore((s) => s.spineData);
  const activeTab = useEditorStore((s) => s.activeTab);
  const setActiveTab = useEditorStore((s) => s.setActiveTab);
  const editingSkuId = useEditorStore((s) => s.editingSkuId);
  const editingToningId = useEditorStore((s) => s.editingToningId);
  const editingColorSetId = useEditorStore((s) => s.editingColorSetId);
  const previewSkinName = useEditorStore((s) => s.previewSkinName);
  const editingSelectionId = useEditorStore((s) => s.editingSelectionId);

  const tabCols = showChildrenTab ? "grid-cols-8" : "grid-cols-7";
  const resolvedTab =
    !showChildrenTab && activeTab === "children" ? "info" : activeTab;

  const detailPanel = useMemo(() => {
    if (
      editingSkuId &&
      templateConfig?.skus?.[editingSkuId]?.category === "static"
    ) {
      return <StaticSkuDetailPanel />;
    }
    if (editingSkuId || previewSkinName) {
      return <SkuDetailPanel />;
    }
    if (editingToningId && editingColorSetId) {
      return <ColorSetDetailPanel />;
    }
    if (editingToningId) {
      return <ToningDetailPanel />;
    }
    if (editingSelectionId) {
      return <SelectionDetailPanel />;
    }
    return null;
  }, [
    editingSkuId,
    previewSkinName,
    editingToningId,
    editingColorSetId,
    editingSelectionId,
    templateConfig?.skus,
  ]);

  const selectionCount = templateConfig?.selection.length || 0;
  const toningCount = templateConfig?.tonings.length || 0;
  const animationCount = templateConfig?.animations.length || 0;
  const skinCount = useMemo(
    () =>
      spineData?.json.skins?.filter((s: any) => s.name !== "default").length ||
      0,
    [spineData],
  );

  return (
    <Tabs
      value={resolvedTab}
      onValueChange={setActiveTab}
      className="h-full flex flex-col gap-4"
    >
      <TabsList className={`grid w-full h-auto ${tabCols}`}>
        <TabsTrigger value="info" className="gap-1 text-xs px-3 py-1.5">
          <Info className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Info</span>
        </TabsTrigger>
        <TabsTrigger value="related" className="gap-1 text-xs px-3 py-1.5">
          <Link2 className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">关联</span>
        </TabsTrigger>
        <TabsTrigger value="selection" className="gap-1 text-xs px-3 py-1.5">
          <Layers className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Selection</span>
          <Badge variant="secondary" className="h-5 px-1.5 text-[10px]">
            {selectionCount}
          </Badge>
        </TabsTrigger>
        <TabsTrigger value="toning" className="gap-1 text-xs px-3 py-1.5">
          <Palette className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">调色</span>
          <Badge variant="secondary" className="h-5 px-1.5 text-[10px]">
            {toningCount}
          </Badge>
        </TabsTrigger>
        <TabsTrigger value="animation" className="gap-1 text-xs px-3 py-1.5">
          <Play className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">动画</span>
          <Badge variant="secondary" className="h-5 px-1.5 text-[10px]">
            {animationCount}
          </Badge>
        </TabsTrigger>
        <TabsTrigger value="skin" className="gap-1 text-xs px-3 py-1.5">
          <Component className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Skin</span>
          <Badge variant="secondary" className="h-5 px-1.5 text-[10px]">
            {skinCount}
          </Badge>
        </TabsTrigger>
        <TabsTrigger value="default-set" className="gap-1 text-xs px-3 py-1.5">
          <Package className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">套装</span>
        </TabsTrigger>
        {showChildrenTab && (
          <TabsTrigger value="children" className="gap-1 text-xs px-3 py-1.5">
            <GitBranch className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">子模版</span>
          </TabsTrigger>
        )}
      </TabsList>

      <div className="flex min-h-0 flex-1 gap-4">
        <TabsContent
          value="related"
          className="mt-0 min-h-0 min-w-0 flex-1 overflow-hidden data-[state=active]:flex data-[state=active]:flex-col data-[state=inactive]:hidden"
        >
          <RelatedTemplatesPanel />
        </TabsContent>

        <Card
          className={`flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden ${
            resolvedTab === "related" ? "hidden" : ""
          }`}
        >
          <CardContent className="min-h-0 flex-1 overflow-y-auto p-4">
            <TabsContent value="info" className="mt-0">
              <InfoPanel />
            </TabsContent>

            <TabsContent value="selection" className="mt-0">
              <SelectionList />
            </TabsContent>

            <TabsContent value="toning" className="mt-0">
              <ToningEditor />
            </TabsContent>

            <TabsContent value="animation" className="mt-0">
              <AnimationEditor />
            </TabsContent>

            <TabsContent value="skin" className="mt-0">
              <SkinViewer />
            </TabsContent>

            <TabsContent value="default-set" className="mt-0">
              <DefaultSetOutfitPanel />
            </TabsContent>

            {showChildrenTab && (
              <TabsContent value="children" className="mt-0">
                <ChildTemplatesPanel />
              </TabsContent>
            )}
          </CardContent>
        </Card>

        {detailPanel && <div className="min-w-0 flex-1">{detailPanel}</div>}
      </div>
    </Tabs>
  );
}
